import { Project } from "models/Project";
import React from "react";
import "./project-timeline.scss";

export type ProjectLifecycleStatus = "upcoming" | "active" | "completed";

type ProjectPeriod = Pick<Project, "start_date" | "end_date">;

interface Props {
    compact?: boolean;
    project: ProjectPeriod;
}

interface ProgressBarProps {
    ariaLabel: string;
    compact?: boolean;
    progress: number;
    tone?: "default" | "danger";
}

export const getProjectProgress = (project: ProjectPeriod, now = Date.now()) => {
    const start = new Date(project.start_date).getTime();
    const end = new Date(project.end_date).getTime();

    if (end <= start) {
        return now >= end ? 100 : 0;
    }

    return Math.max(0, Math.min(100, ((now - start) / (end - start)) * 100));
};

export const getProjectLifecycleStatus = (
    project: ProjectPeriod,
    now = Date.now(),
): ProjectLifecycleStatus => {
    if (now < new Date(project.start_date).getTime()) {
        return "upcoming";
    }

    if (now >= new Date(project.end_date).getTime()) {
        return "completed";
    }

    return "active";
};

export const ProjectProgressBar: React.FC<ProgressBarProps> = ({
    ariaLabel,
    compact = false,
    progress,
    tone = "default",
}) => {
    const visualProgress = Math.max(0, Math.min(100, progress));
    return (
        <div
            aria-label={ariaLabel}
            className={[
                "project-timeline",
                compact ? "project-timeline--compact" : "",
                tone === "danger" ? "project-timeline--danger" : "",
            ].filter(Boolean).join(" ")}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(visualProgress)}
        >
            <div className="project-timeline__fill" style={{ width: `${visualProgress}%` }} />
            <span className="project-timeline__marker" style={{ left: `${visualProgress}%` }} />
        </div>
    );
};

const ProjectTimeline: React.FC<Props> = ({ compact = false, project }) => {
    const progress = getProjectProgress(project);
    return <ProjectProgressBar
        ariaLabel={`${Math.round(progress)}% voortgang`}
        compact={compact}
        progress={progress}
    />;
};

export default ProjectTimeline;
